import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  UsersIcon,
  ClockIcon,
  MapPinIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const GroupOrderForm = ({ isOpen, onClose, onSuccess, restaurants }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    restaurantId: '',
    restaurant: null,
    orderDeadline: '',
    deliveryTime: '',
    deliveryInfo: {
      location: '',
      phoneNumber: '',
      landmark: ''
    },
    maxParticipants: 10,
    minOrderAmount: 0,
    splitMethod: 'individual',
    isPublic: true
  });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [universityLandmarks, setUniversityLandmarks] = useState([]);
  const [landmarksLoading, setLandmarksLoading] = useState(false);

  const fetchLandmarks = useCallback(async (universityId) => {
    if (!universityId) {
      setUniversityLandmarks([]);
      return;
    }
    setLandmarksLoading(true);
    try {
      const response = await axios.get(
        `https://mongobyte-hc73.onrender.com/api/v1/universities/${universityId}/landmarks`
      );
      setUniversityLandmarks(response.data.landmarks || []);
    } catch (error) {
      console.error('Error fetching landmarks:', error);
      setUniversityLandmarks([]);
    } finally {
      setLandmarksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Get user data
      const userData = localStorage.getItem('byteUser');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setFormData(prev => ({
          ...prev,
          deliveryInfo: {
            location: parsedUser.location || '',
            phoneNumber: parsedUser.phoneNumber || '',
            landmark: parsedUser.nearestLandmark || ''
          }
        }));
        
        // Fetch landmarks if user has a university
        if (parsedUser.university?._id) {
          fetchLandmarks(parsedUser.university._id);
        }
      }
    }
  }, [isOpen, fetchLandmarks]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.restaurantId) {
      showErrorNotification('Please select a restaurant for your group order');
      return;
    }

    if (!formData.title.trim()) {
      showErrorNotification('Please enter a title for your group order');
      return;
    }

    if (!formData.orderDeadline) {
      showErrorNotification('Please set an order deadline');
      return;
    }

    if (new Date(formData.orderDeadline) <= new Date()) {
      showErrorNotification('Order deadline must be in the future');
      return;
    }

    if (!formData.deliveryInfo.location.trim()) {
      showErrorNotification('Please enter a delivery location');
      return;
    }

    if (!formData.deliveryInfo.phoneNumber.trim()) {
      showErrorNotification('Please enter a phone number');
      return;
    }

    setLoading(true);
    
    try {
      // Format the data according to the API specification
      const requestData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        restaurant: formData.restaurantId,
        orderDeadline: formData.orderDeadline,
        deliveryTime: formData.deliveryTime || undefined,
        deliveryInfo: {
          location: formData.deliveryInfo.location.trim(),
          phoneNumber: formData.deliveryInfo.phoneNumber.trim(),
          nearestLandmark: formData.deliveryInfo.landmark.trim() || undefined
        },
        maxParticipants: formData.maxParticipants,
        minOrderAmount: formData.minOrderAmount,
        splitMethod: formData.splitMethod,
        isPublic: formData.isPublic
      };

      const response = await groupOrdersApi.createGroupOrder(requestData);
      
      if (response.success) {
        showSuccessNotification('Group order created successfully!');
        onSuccess(response.data);
      } else {
        showErrorNotification(response.message || 'Failed to create group order');
      }
    } catch (error) {
      console.error('Create group order error:', error);
      showErrorNotification('Failed to create group order');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-accentwhite rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-secondary-200">
            <h2 className="text-2xl font-bold text-crust font-display">Create Group Order</h2>
            <button
              onClick={onClose}
              className="text-olive-light hover:text-pepperoni p-2 rounded-xl hover:bg-secondary-50 transition-all duration-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-crust font-display">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-bold text-crust font-display mb-2">
                  Restaurant *
                </label>
                <select
                  value={formData.restaurantId}
                  onChange={(e) => {
                    const selected = restaurants.find(r => r._id === e.target.value);
                    handleInputChange('restaurantId', e.target.value);
                    handleInputChange('restaurant', selected);
                  }}
                  required
                  className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                >
                  <option value="">Select a restaurant</option>
                  {Array.isArray(restaurants) && restaurants.map((restaurant) => (
                    <option key={restaurant._id} value={restaurant._id}>
                      {restaurant.name} - {restaurant.university?.name || 'Unknown University'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-olive-light font-sans mt-1">
                  All participants will order from this restaurant
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-crust font-display mb-2">
                  Group Order Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Friday Office Lunch, Movie Night Snacks"
                  required
                  className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-crust font-display mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Tell people what this group order is about..."
                  rows={3}
                  className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans resize-none"
                />
              </div>
            </div>

            {/* Timing */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-crust font-display flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-cheese" />
                Timing
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Order Deadline *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.orderDeadline}
                    onChange={(e) => handleInputChange('orderDeadline', e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    required
                    className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                  />
                  <p className="text-xs text-olive-light font-sans mt-1">
                    When should ordering close?
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Delivery Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.deliveryTime}
                    onChange={(e) => handleInputChange('deliveryTime', e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                  />
                  <p className="text-xs text-olive-light font-sans mt-1">
                    When should food arrive?
                  </p>
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-crust font-display flex items-center gap-2">
                <MapPinIcon className="w-5 h-5 text-cheese" />
                Delivery Information
              </h3>
              
              <div>
                <label className="block text-sm font-bold text-crust font-display mb-2">
                  Delivery Location *
                </label>
                <input
                  type="text"
                  value={formData.deliveryInfo.location}
                  onChange={(e) => handleInputChange('deliveryInfo.location', e.target.value)}
                  placeholder="Where should the food be delivered?"
                  required
                  className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.deliveryInfo.phoneNumber}
                    onChange={(e) => handleInputChange('deliveryInfo.phoneNumber', e.target.value)}
                    required
                    className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Nearest Landmark
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      value={formData.deliveryInfo.landmark}
                      onChange={(e) => handleInputChange('deliveryInfo.landmark', e.target.value)}
                      className="w-full p-3 pl-10 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans appearance-none bg-white"
                    >
                      <option value="">Select a landmark</option>
                      {landmarksLoading ? (
                        <option disabled>Loading landmarks...</option>
                      ) : (
                        universityLandmarks.map((landmark, index) => (
                          <option key={index} value={landmark}>
                            {landmark}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Group Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-crust font-display flex items-center gap-2">
                <UsersIcon className="w-5 h-5 text-cheese" />
                Group Settings
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value))}
                    min="2"
                    max="50"
                    className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-crust font-display mb-2">
                    Minimum Order Amount (₦)
                  </label>
                  <input
                    type="number"
                    value={formData.minOrderAmount}
                    onChange={(e) => handleInputChange('minOrderAmount', parseInt(e.target.value) || 0)}
                    min="0"
                    step="100"
                    className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-crust font-display mb-2">
                  Cost Splitting Method
                </label>
                <select
                  value={formData.splitMethod}
                  onChange={(e) => handleInputChange('splitMethod', e.target.value)}
                  className="w-full p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                >
                  <option value="individual">Individual - Each person pays for their own items</option>
                  <option value="equal">Equal Split - Divide total cost equally among all participants</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                  className="rounded border-secondary-300 text-pepperoni focus:ring-cheese"
                />
                <label htmlFor="isPublic" className="text-sm font-sans text-olive-light">
                  Make this group order public (others can discover and join)
                </label>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-secondary-50 rounded-xl p-4 border-2 border-secondary-200">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="w-5 h-5 text-cheese mt-0.5" />
                <div className="text-sm font-sans text-olive-light">
                  <p className="font-bold text-crust mb-2">How Group Orders Work:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Create the group order and share with friends</li>
                    <li>Participants join and add their meals</li>
                    <li>Everyone pays for their share before the deadline</li>
                    <li>Order is automatically placed when everyone has paid</li>
                    <li>Food is delivered to the specified location</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white pb-4 pt-4 border-t border-secondary-200 -mx-6 px-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="w-full sm:flex-1 py-3 px-6 border-2 border-secondary-300 text-olive-light rounded-xl font-bold font-display hover:bg-secondary-50 transition-all duration-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:flex-1 py-3 px-6 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accentwhite"></div>
                      <span>Creating...</span>
                    </div>
                  ) : (
                    'Create Group Order'
                  )}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GroupOrderForm;